import React, { useState, useCallback, useMemo } from 'react';
import './ConversationList.css';

/**
 * ConversationList Component
 * 
 * Displays a list of all active conversations with search and filtering
 * Shows unread counts, last message preview, and search by teacher name or message content
 * 
 * @param {Array} conversations - List of conversation objects
 * @param {string} selectedConversation - Currently selected conversation ID
 * @param {Function} onSelectConversation - Callback when conversation is selected
 * @param {boolean} isLoading - Loading state
 * @param {Function} onLoadMore - Callback for pagination
 */
const ConversationList = ({ 
  conversations, 
  selectedConversation, 
  onSelectConversation, 
  isLoading,
  onLoadMore 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounced search handler (300ms delay)
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(value.toLowerCase());
    }, 300);

    setSearchTimeout(timeout);
  }, [searchTimeout]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  }, [searchTimeout]);

  // Filter conversations based on search term
  const filteredConversations = useMemo(() => {
    if (!debouncedSearchTerm) {
      return conversations;
    }

    return conversations.filter(conv => {
      const teacherName = (conv.other_user_name || '').toLowerCase();
      const lastMessage = (conv.last_message || '').toLowerCase();
      
      return (
        teacherName.includes(debouncedSearchTerm) || 
        lastMessage.includes(debouncedSearchTerm)
      );
    });
  }, [conversations, debouncedSearchTerm]);

  // Highlight search term in text
  const highlightText = (text, term) => {
    if (!term || !text) return text;

    const parts = text.split(new RegExp(`(${term})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === term.toLowerCase() ? (
        <mark key={index}>{part}</mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className="conversation-list">
      {/* Search/Filter Bar with enhanced functionality */}
      <div className="conversation-list-header">
        <input 
          type="text" 
          placeholder="Search conversations..."
          className="conversation-search"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        {searchTerm && (
          <button 
            className="clear-search-btn"
            onClick={handleClearSearch}
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* Conversations */}
      <div className="conversation-items">
        {isLoading && conversations.length === 0 && (
          <div className="conversation-loading">
            <div className="mini-spinner"></div>
            <p>Loading conversations...</p>
          </div>
        )}

        {!isLoading && filteredConversations.length === 0 && (
          <div className="conversation-empty">
            <p>
              {debouncedSearchTerm 
                ? `No conversations match "${debouncedSearchTerm}"`
                : 'No conversations yet'}
            </p>
          </div>
        )}

        {filteredConversations.map(conv => (
          <div
            key={conv.conversation_id}
            className={`conversation-item ${selectedConversation === conv.conversation_id ? 'active' : ''}`}
            onClick={() => onSelectConversation(conv.conversation_id)}
          >
            {/* Avatar */}
            <div className="conversation-avatar">
              {conv.other_user_name.charAt(0).toUpperCase()}
            </div>

            {/* Conversation Info */}
            <div className="conversation-info">
              <div className="conversation-header">
                <h3 className="conversation-name">
                  {highlightText(conv.other_user_name, debouncedSearchTerm)}
                </h3>
                {conv.unread_count > 0 && (
                  <span className="unread-badge">{conv.unread_count}</span>
                )}
              </div>
              <p className="conversation-preview">
                {highlightText(
                  conv.last_message 
                    ? conv.last_message.substring(0, 40)
                    : 'No messages',
                  debouncedSearchTerm
                )}
                {conv.last_message && conv.last_message.length > 40 ? '...' : ''}
              </p>
              <p className="conversation-time">{conv.last_message_time}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {filteredConversations.length > 0 && (
        <div className="conversation-load-more">
          <button onClick={onLoadMore} className="load-more-btn">
            Load more conversations
          </button>
        </div>
      )}
    </div>
  );
};

export default ConversationList;
